import React, { ReactNode } from "react";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { Icon } from "assets/Icon";
import { ToggleButton } from "../../../../../../components/ToggleButton";
import ModalDeleteSection from "newui/features/Rfx/Creation/components/ReturnableSchedule/ModalDeleteSection/ModalDeleteSection";
import { useField, FieldHelperProps, FieldMetaProps } from "formik";
import { cn } from "utils/helpers";
import styled from "styled-components";
import tw from "twin.macro";
import Tooltip from "components/Tooltip";
import { QUESTION_TYPE_ID } from "newui/features/Rfx/Submission/constants";

interface CardQuestionerStyledProps {
	className?: string;
	hasError?: boolean;
	isSection?: boolean;
	isPreview?: boolean;
	isQuote?: boolean;
}

const CardQuestionerStyled = styled.div<CardQuestionerStyledProps>(
	({ className, isSection, isPreview, isQuote, hasError }) => [
		tw`relative w-full flex flex-col bg-white pt-4 pb-0.5 px-8 h-full min-h-20 rounded-md border border-gray-200 relative mb-8`,
		isSection ? tw`border-t-8 border-t-royal-blue` : tw`mx-auto w-[95%] mb-4`,
		(isPreview || isQuote) && tw`pt-3 mx-0 w-full`,
		hasError && tw`border-red-500`,
		// Apply outer border to the card
		`
			transition: border-color 0.2s, box-shadow 0.2s;
			&:focus-within {
				box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.47);
				border: none;
			}
		`,
		className,
	]
);

interface QuestionCardProps {
	children?: ReactNode;
	isSection?: boolean;
	isSmartField?: boolean;
	dragHandle?: Record<string, unknown>;
	divider?: boolean;
	className?: string;
	index?: number;
	sectionIndex?: number;
	copyQuestionHandler?: () => void;
	deleteQuestionHandler?: () => void;
	copySectionHandler?: () => void;
	deleteSectionHandler?: () => void;
	copyQuotesHandler?: () => void;
	deleteQuotesHandler?: () => void;
	isQuote?: boolean;
	label?: string;
	isPreview?: boolean;
	isDeletable?: boolean;
}

export const QuestionCard: React.FC<QuestionCardProps> = ({
	children,
	isSection = false,
	isSmartField,
	dragHandle,
	divider = true,
	className,
	index = 0,
	sectionIndex = 0,
	copyQuestionHandler,
	deleteQuestionHandler,
	copySectionHandler,
	deleteSectionHandler,
	copyQuotesHandler,
	deleteQuotesHandler,
	isQuote = false,
	label,
	isPreview = false,
	isDeletable = false,
	...props
}) => {
	const { isOpen, onClose } = useDisclosure();

	const fieldname = (): string =>
		isQuote
			? `quote[${sectionIndex}]`
			: isSection
				? `sections[${sectionIndex}]`
				: `sections[${sectionIndex}]questionnaire[${index}]`;

	const [, meta, helper]: [
		unknown,
		FieldMetaProps<any>,
		FieldHelperProps<any>,
	] = useField(fieldname());

	const setRequiredHandler = (): void => {
		helper.setValue({
			...meta.value,
			required: isQuote ? 1 : meta.value?.required ? 0 : 1,
		});
	};

	const setCommentValue = (commentValue: 1 | 0) => {
		helper.setValue({
			...meta.value,
			hasComment: commentValue,
		});
	};

	const setHasCommentHandler = (): void => {
		setCommentValue(meta.value?.hasComment ? 0 : 1);
	};

	const canHaveComment =
		meta.value?.type !== QUESTION_TYPE_ID.PARAGRAPH &&
		meta.value?.type !== QUESTION_TYPE_ID.SHORT_ANSWER;

	const hasError =
		meta?.error !== null && meta?.error !== "" && meta?.error !== undefined;

	return (
		<>
			<CardQuestionerStyled
				className={`${className || ""} card-shadow`}
				hasError={hasError}
				isQuote={isQuote}
				isSection={isSection}
				isPreview={isPreview}
				{...props}
			>
				{label && (
					<h4
						className="absolute left-8 top-4 flex gap-1 font-roboto text-xl font-bold text-primary-700"
						data-testid={`${fieldname()}label`}
					>
						{label}
						<div className="font-roboto font-medium text-required">
							<span>*</span>
						</div>
					</h4>
				)}

				{!isPreview && (
					<div
						className="absolute left-1/2 top-4 -translate-x-1/2"
						{...dragHandle}
					>
						<Icon
							icon="drag"
							style={{
								marginLeft: "auto",
								marginRight: "auto",
								cursor: "grab",
							}}
						/>
					</div>
				)}

				<div className="my-3 mb-0">{children}</div>

				{divider && (
					<div className="mt-3 w-full border-b border-gray-200 text-gray-200" />
				)}

				{!isPreview && (
					<div
						className={cn("min-h-16 flex-grow", isSection && "min-h-[52px]")}
					>
						<Flex
							gap={5}
							align="center"
							className={`relative float-left ${isSection ? "my-4" : "my-2"}`}
						>
							<Tooltip label="Copy Question">
								<Icon
									icon="copy"
									style={{ cursor: "pointer" }}
									onClick={
										isQuote
											? copyQuotesHandler
											: isSection
												? copySectionHandler
												: copyQuestionHandler
									}
								/>
							</Tooltip>
						</Flex>
						<Flex
							gap={5}
							align="center"
							className={`relative float-right ${isSection ? "my-4" : "my-2"}`}
						>
							{isDeletable && (
								<Icon
									icon="trash-stroke"
									style={{ cursor: "pointer" }}
									iconClassName="text-red-600"
									onClick={
										isQuote
											? deleteQuotesHandler
											: isSection
												? deleteSectionHandler
												: deleteQuestionHandler
									}
								/>
							)}

							{!isQuote && (
								<>
									{(!isSection || isSmartField) && (
										<>
											{canHaveComment && (
												<Tooltip label="Makes the question provide an optional comment field for the Supplier to add any additional information">
													<div className="flex h-6 items-center border-l pl-3">
														<ToggleButton
															label="Allow Comment"
															onChange={setHasCommentHandler}
															checked={meta?.value?.hasComment}
														/>
													</div>
												</Tooltip>
											)}
											<Tooltip label="Makes the question required for the Supplier to complete">
												<div className="flex h-6 items-center border-l pl-3">
													<ToggleButton
														label="Required"
														onChange={setRequiredHandler}
														checked={meta?.value?.required}
													/>
												</div>
											</Tooltip>
										</>
									)}
								</>
							)}
						</Flex>
					</div>
				)}
			</CardQuestionerStyled>

			<ModalDeleteSection
				isOpen={isOpen}
				onClose={onClose}
			/>
		</>
	);
};

export default QuestionCard;
